import { RenderEngine } from 'application-frame/rendering';

/**
 * animates an element between two states
 *
 * @param  {HTMLElement} target      The target element to operate on
 * @param  {string[]} targetClasses  All classes of the target state
 * @param  {} removeClasses          Classes which should be removed to reach the target state
 * @param  {string[]} [properties=[]] Css properties beside position and dimention that should be animated
 * @param  {Number} [duration=0.4]    Duration of the animation
 * @param  {Function} whenFirstDone   A callback for when the first stage is done
 * @param  {Element} actor
 *
 * @return {Promise<any>}             Returns a promise which resolves once the animation is done
 */
const flip = function({ target, targetClasses, removeClasses, properties = [],
    duration = 0.4, whenFirstDone, actor }) {
    return new Promise((done) => {
        actor = actor || target;
        const startStyle = createStyleTree(actor);

        processParentNode(actor, startStyle, { properties });

        Array.prototype.forEach.apply(actor.children, [childNode => {
            const style = createStyleTree(childNode, startStyle);

            processChildNode(childNode, style);
            style.parent.children.push(style);
        }]);

        // we got all the current information. time to go to last
        if (targetClasses) {
            target.classList.add(...targetClasses);
        }

        if (removeClasses) {
            target.classList.remove(...removeClasses);
        }

        if (typeof whenFirstDone === 'function') {
            whenFirstDone();
        }

        calculateDifference(startStyle, { properties, duration, done });
    });
};

export default flip;

/**
 * processes the parent node of the target
 * @param  {Node} node         the node to operate on
 * @param  {Object} tree       the css tree
 * @param  {string[]} properties css properties to animate
 * @return {void}
 */
const processParentNode = function(node, tree, { properties }) {
    const box = node.getBoundingClientRect();
    const style = window.getComputedStyle(node);

    properties;
    tree.position.x = box.left;
    tree.position.y = box.top;
    tree.size.height = box.height;
    tree.size.width = box.width;

    properties.forEach(key => {
        tree[key] = style[key];
    });
};

const processChildNode = function(node, tree) {
    tree.position.x = node.offsetLeft;
    tree.position.y = node.offsetTop;
    tree.size.height = node.offsetHeight;
    tree.size.width = node.offsetWidth;
};

const createStyleTree = function(node, parent) {
    return {
        node: node,
        position: {},
        size: {},
        parent: parent,
        children: [],
    };
};

const calculateDifference = function(styleTree, { properties, duration, done }) {

    const box = styleTree.node.getBoundingClientRect();
    const ownMatrix = window.getComputedStyle(styleTree.node).transform
        .match(/matrix\(([0-9.-]+), ([0-9.-]+), ([0-9.-]+), ([0-9.-]+), ([0-9.-]+), ([0-9.-]+)\)/) || [0, 1, 0, 0, 1, 0, 0];

    const [, , , , , ownTranslateX, ownTranslateY] = ownMatrix.map(item => parseInt(item));
    const parentScaleX = styleTree.size.width / box.width;
    const parentScaleY = styleTree.size.height / box.height;

    styleTree.translateX = (styleTree.position.x - box.left) + ownTranslateX;
    styleTree.translateY = (styleTree.position.y - box.top) + ownTranslateY;
    styleTree.scaleX = parentScaleX;
    styleTree.scaleY = parentScaleY;

    styleTree.children.forEach(current => {
        current.translateX = current.position.x - current.node.offsetLeft;
        current.translateY = current.position.y - current.node.offsetTop;
        current.scaleX = current.size.width / (current.node.offsetWidth * parentScaleX);
        current.scaleY = current.size.height / (current.node.offsetHeight * parentScaleY);
        current.node.style.transformOrigin = 'top left';
        current.node.style.transform = `translate3d(${current.translateX}px, ${current.translateY}px, 0) scale(${current.scaleX}, ${current.scaleY})`;
    });

    styleTree.node.style.transform = `translate3d(${styleTree.translateX}px, ${styleTree.translateY}px, 0) scale(${styleTree.scaleX}, ${styleTree.scaleY})`;
    styleTree.node.style.transformOrigin = 'top left';


    properties.forEach(key => {
        styleTree.node.style.setProperty(key, styleTree[key]);
    });

    RenderEngine.skipFrame().scheduleRenderTask(applyTransition.bind(null, styleTree, { properties, duration, done }));

//    applyTransition(styleTree, { properties, duration, done });
};

const applyTransition = function(styleTree, { properties, duration, done }) {

    applyTransitionCss(styleTree, { properties, duration });

    styleTree.children.forEach(child => applyTransitionCss(child, { duration }));

    RenderEngine.skipFrame().scheduleRenderTask(playAnimation.bind(null, styleTree, { properties, done }));
};

const applyTransitionCss = function(tree, { duration, properties = [] }) {
    properties.unshift('transform');
    tree.node.style.transition = properties.map(key => `${key} ${duration}s ease-out`).join(',');
};

const playAnimation = function(styleTree, { properties, done }) {

    styleTree.node.addEventListener('transitionend', function animationDone() {
        styleTree.node.removeEventListener('transitionend', animationDone, true);

        styleTree.node.style.transition = '';
        styleTree.children.forEach(child => {
            child.node.style.transition = '';
        });

        done();
    }, { passive: true });

    properties.forEach(key => {
        styleTree.node.style.setProperty(key, '');
    });

    styleTree.children.forEach(child => {
        child.node.style.transform = '';
    });
};



//////////////////
// WEBPACK FOOTER
// ./flip.js
// module id = 59
// module chunks = 0